import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Metadata, MetadataColumn } from '../models/metadata';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { TableType } from '../models/shipment';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  private metadata$: Observable<Metadata> | null = null;
  private readonly dataSources: { [key: string]: keyof Metadata } = {
    Shipments: 'shipmentColumns',
    Containers: 'containerColumns',
    Orders: 'orderColumns',
    OrderExceptions: 'orderExceptionColumns'
  };

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  getMetadata() {
    if (!this.metadata$) {
      this.metadata$ = this.http.get<Metadata>(`${environment.apiUrl}/metadata`).pipe(shareReplay(1));
    }
    return this.metadata$;
  }

  getFilteredOutMetadata() {
    return combineLatest([this.getMetadata(), this.userService.getUserInfo()]).pipe(
      map(([metadata, { type }]) => {
        const filterPredicate = ({ customFieldType }: MetadataColumn) =>
          type !== 'External' || customFieldType !== 'Internal';
        return {
          ...metadata,
          shipmentColumns: metadata.shipmentColumns.filter(filterPredicate),
          containerColumns: metadata.containerColumns.filter(filterPredicate),
          orderColumns: metadata.orderColumns.filter(filterPredicate),
          orderExceptionColumns: metadata.orderExceptionColumns.filter(filterPredicate)
        };
      })
    );
  }

  getColumnsBy(keys: MetadataColumn['key'][], type: TableType) {
    return this.getMetadata().pipe(
      map(metadata => metadata[this.dataSources[type]].filter(({ key }) => keys.includes(key)) as MetadataColumn[])
    );
  }

  getColumnNameBy(columnKey: string, type: TableType) {
    return this.getMetadata().pipe(
      map(metadata => metadata[this.dataSources[type]].find(({ key }) => key === columnKey)?.name || '')
    );
  }

  getColumnDataTypeBy(columnKey: string, type: TableType) {
    return this.getMetadata().pipe(
      map(
        metadata =>
          (metadata[this.dataSources[type]].find(({ key }) => key === columnKey) as MetadataColumn)?.dataType || ''
      )
    );
  }
}
